import React, { useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { useQuiz } from '../context/QuizContext';
import SafeIcon from '../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';

const { FiRefreshCw, FiCheckCircle, FiZap, FiTarget, FiInfo, FiMessageSquare } = FiIcons;

const ResultScreen = () => {
  const navigate = useNavigate();
  const { userInfo, getRecommendation, resetQuiz } = useQuiz();
  const result = getRecommendation();

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  return (
    <div className="min-h-screen py-10 px-4 flex justify-center font-sans">
      <motion.div 
        initial={{ opacity: 0, y: 30 }}
        animate={{ opacity: 1, y: 0 }}
        className="max-w-3xl w-full bg-white rounded-3xl shadow-2xl overflow-hidden border-t-8 border-brand-blue"
      >
        <div className={`p-10 text-center bg-brand-gray/30 border-b border-gray-100`}>
          <h2 className="text-sm font-bold uppercase tracking-[0.2em] text-brand-red mb-4">
            Deine Analyse für {userInfo.name}
          </h2>
          <h1 className="text-4xl md:text-6xl font-display text-brand-blue leading-none mb-4 uppercase">
            {result.title}
          </h1>
        </div>

        <div className="p-8 md:p-12 space-y-12">
          <section className="relative">
            <div className="absolute -left-6 top-0 bottom-0 w-1.5 bg-brand-red"></div>
            <p className="text-xl text-gray-800 leading-relaxed font-medium italic pl-4">
              "{result.description}"
            </p>
          </section>

          <div className="grid md:grid-cols-2 gap-8">
            <section className="bg-brand-gray/50 rounded-2xl p-6 border border-brand-blue/5">
              <h3 className="text-lg font-display uppercase tracking-wider text-brand-blue mb-4 flex items-center">
                <SafeIcon icon={FiInfo} className="mr-2 text-brand-red" />
                Symptom-Muster
              </h3>
              <ul className="space-y-3">
                {result.signs.map((sign, i) => (
                  <li key={i} className="flex items-start text-sm text-gray-700">
                    <SafeIcon icon={FiCheckCircle} className="mt-1 mr-3 text-brand-blue flex-shrink-0" />
                    <span>{sign}</span>
                  </li>
                ))}
              </ul>
            </section>

            <section className="bg-brand-blue text-white rounded-2xl p-6 shadow-xl">
              <h3 className="text-lg font-display uppercase tracking-wider mb-4 flex items-center">
                <SafeIcon icon={FiZap} className="mr-2 text-brand-red" />
                Sofort-Reset
              </h3>
              <p className="text-sm opacity-90 leading-relaxed">{result.reset}</p>
            </section>
          </div>

          <section>
            <h3 className="text-xl font-display uppercase tracking-wider text-brand-blue mb-6 flex items-center justify-center">
              <SafeIcon icon={FiTarget} className="mr-3 text-brand-red" />
              Deine 3 Hebel zur Stabilität
            </h3>
            <div className="grid md:grid-cols-3 gap-4">
              {result.levers.map((lever, i) => (
                <div key={i} className="bg-white border-2 border-brand-gray p-5 rounded-xl hover:border-brand-blue/20 transition-all text-center">
                  <div className="text-3xl font-display text-brand-red/20 mb-2">#{i+1}</div>
                  <p className="text-sm text-brand-blue font-bold leading-snug">{lever}</p>
                </div>
              ))}
            </div>
          </section>

          <section className="pt-10 border-t border-gray-100">
            <h3 className="text-center font-display text-3xl text-brand-blue mb-8 uppercase tracking-tight">Wie geht es jetzt weiter?</h3>
            <div className="grid md:grid-cols-2 gap-4">
              {result.cta.map((cta, i) => (
                <a
                  key={i}
                  href={cta.href || "#"}
                  className={`group flex flex-col items-center justify-center p-8 rounded-2xl transition-all text-center shadow-sm border-2
                    ${cta.href 
                      ? 'border-brand-red/20 bg-brand-red/5 hover:border-brand-red hover:bg-white' 
                      : 'border-brand-blue/10 bg-brand-blue/5 hover:border-brand-blue hover:bg-white'
                    }`}
                >
                  <div className={`w-14 h-14 rounded-full flex items-center justify-center mb-4 text-white shadow-lg group-hover:scale-110 transition-transform
                    ${cta.href ? 'bg-brand-red' : 'bg-brand-blue'}`}>
                    <SafeIcon icon={FiMessageSquare} className="text-2xl" />
                  </div>
                  <span className="font-display text-xl text-brand-blue uppercase tracking-wider">{cta.text}</span>
                  <span className="text-xs text-gray-500 mt-2 font-medium">{cta.sub}</span>
                </a>
              ))}
            </div>
          </section>

          <footer className="text-center pt-10">
            <button 
              onClick={() => { resetQuiz(); navigate('/'); }}
              className="text-gray-400 hover:text-brand-red flex items-center justify-center mx-auto text-xs uppercase tracking-[0.2em] transition-colors"
            >
              <SafeIcon icon={FiRefreshCw} className="mr-2" />
              Check neu starten
            </button>
          </footer>
        </div>
      </motion.div>
    </div>
  );
};

export default ResultScreen;